import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import Spin from 'antd/lib/spin';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Card from 'antd/lib/card';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Table from 'antd/lib/table';
import Modal from 'antd/lib/modal';
import Tag from 'antd/lib/tag';
import Input from 'antd/lib/input';

const Option = Select.Option;

// helpers (older-safe)
function MISSING(t) { return t('Údaj neposkytnutý bankou'); }
function first() {
  for (var i = 0; i < arguments.length; i++) {
    var v = arguments[i];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}
function pretty(obj) { try { return JSON.stringify(obj, null, 2); } catch(e) { return String(obj); } }
function ymKey(dStr) {
  if (!dStr) return '';
  var d = new Date(dStr);
  if (isNaN(d.getTime())) return '';
  return d.getFullYear() + '-' + (d.getMonth() + 1);
}
function parseYmKey(k) {
  var p = (k || '').split('-');
  return { year: parseInt(p[0], 10), month: parseInt(p[1], 10) };
}
function sortYmDesc(a, b) {
  var pa = parseYmKey(a), pb = parseYmKey(b);
  if (pa.year !== pb.year) return pb.year - pa.year;
  return pb.month - pa.month;
}

/**
 * Slovak formatting via Intl:
 * - thousands: space ' '
 * - decimal: comma ','
 * - 2 decimal places (rounded)
 */
export function formatAmountSk(n) {
  if (typeof n !== 'number' || !isFinite(n)) return '';

  const formatted = new Intl.NumberFormat('sk-SK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  return formatted;
}

function getAccountIban(acc, iban) {
  if (typeof acc === 'string') {
    return acc
  }
  if (typeof acc === 'object' && acc && 'iban' in acc){
    return acc.iban
  }
  return iban;
}

function normIban(s, iban) {
  const acctIban = getAccountIban(s, iban);
  return (acctIban || '').toString().replace(/\s+/g, '').toUpperCase();
}

function getCounterPartyIban(tr, iban) {
  const creditorAccount = normIban(tr.creditorAccount, iban);
  const debtorAccount = normIban(tr.debtorAccount, iban);
  return creditorAccount !== iban ? creditorAccount : (debtorAccount !== iban ? debtorAccount : '-');
}

function getCounterParty(tr, iban) {
  const debtorAccount = normIban(tr.debtorAccount);
  if (tr.amount < 0 || debtorAccount === iban) {
    return tr.creditorName ? tr.creditorName : '-'
  }
  return tr.debtorName ? tr.debtorName : '-'
}

@translate()
export default class Psd2StatementsAdmin extends Component {
  static propTypes = {
    // state flags
    loading: PropTypes.bool,

    // from container/ducks
    accountIds: PropTypes.array.isRequired,     // array of accountId strings (or objects with {id,label})
    selectedAccountId: PropTypes.string,        // optional initial selection
    ibans:PropTypes.array,                      // [{ accountId, IBAN }] mapper
    accountData: PropTypes.object,              // PSD2AccountData (may include balances via default scope)
    transactions: PropTypes.array,              // ALL transactions for selected account (we filter by month locally)
    categories: PropTypes.array,                // PSD2 categories [{id,name,description?...}]
    criteria: PropTypes.array,                  // PSD2 aggregation criteria [{id,provider,iban,searchString,categoryId}]
    hasAny: PropTypes.bool,                     // quick flag if any PSD2 tx exist

    // effects
    fetchAccountData: PropTypes.func.isRequired,    // (accountId)
    fetchAllTx: PropTypes.func.isRequired,          // ({accountId, pageNo?, pageSize?})
    fetchCategories: PropTypes.func.isRequired,     // ()
    fetchCriteria: PropTypes.func.isRequired,       // ()
    onChangeAccount: PropTypes.func.isRequired,     // (accountId) -> container stores local state

    // destructive action (container knows which requisitions to purge)
    onPurgeTriggered: PropTypes.func,               // () => Promise|void

    // i18n
    t: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedAccountId: props.selectedAccountId || undefined,
      selectedYm: undefined, // "YYYY-M"
      showAccountJson: false,
      showOwnerJson: false,
      ownerJsonCache: null,
      monthsForAccount: [],  // array of "YYYY-M" for currently selected account

      // purge modal
      purgeVisible: false,
      purgeConfirmText: '',
      purgeLoading: false,
    };
  }

  componentDidMount() {
    // load static ref data
    this.props.fetchCategories({});
    this.props.fetchCriteria({});

    // pick initial account
    var aid = this.state.selectedAccountId || this._firstAccountId(this.props.accountIds);
    if (aid) {
      this._selectAccount(aid);
    }
  }

  componentDidUpdate(prevProps) {
    // external account selection change → update internal + fetch
    if (this.props.selectedAccountId && this.props.selectedAccountId !== prevProps.selectedAccountId) {
      if (this.props.selectedAccountId !== this.state.selectedAccountId) {
        this._selectAccount(this.props.selectedAccountId);
      }
    }

    // when transactions for current account arrive, recompute months + default select latest
    if (this.props.transactions !== prevProps.transactions) {
      this._rebuildMonthsAndMaybePickDefault();
    }
  }

  _firstAccountId(list) {
    if (!list || !list.length) return undefined;
    var a = list[0];
    return (typeof a === 'string') ? a : (a && a.id) ? a.id : undefined;
  }

  _selectAccount = (accountId) => {
    this.setState({ selectedAccountId: accountId, selectedYm: undefined, monthsForAccount: [] });
    // notify container (it keeps selected id too)
    this.props.onChangeAccount(accountId);
    // fetch required data
    this.props.fetchAccountData(accountId);
    this.props.fetchAllTx({ accountId: accountId, pageSize: 2000 });
  };

  _rebuildMonthsAndMaybePickDefault = () => {
    var tx = this.props.transactions || [];
    var set = {};
    for (var i = 0; i < tx.length; i++) {
      var k = ymKey(tx[i].bookingDate);
      if (k) set[k] = true;
    }
    var months = Object.keys(set).sort(sortYmDesc);
    var nextState = { monthsForAccount: months };
    // default month if none selected
    if (!this.state.selectedYm && months.length > 0) {
      nextState.selectedYm = months[0];
    }
    this.setState(nextState);
  };

  _monthLabel(k) {
    var p = parseYmKey(k);
    if (!p.year || !p.month) return k;
    return (p.month + '/' + p.year);
  }

  // ========= detail builders =========

  _buildAccountDetail = () => {
    var t = this.props.t;
    var accountData = this.props.accountData || {};
    var all = accountData.allData || {};
    var balances = [];
    if (accountData && accountData.balances) balances = accountData.balances;

    var bank = first(all.bic, accountData.institution_id, MISSING(t));
    var accountName = first(all.displayName, accountData.name, all.name, MISSING(t));
    var currency = first(accountData.currency, all.currency, MISSING(t));
    var iban = first(accountData.iban, all.iban, MISSING(t));

    var balanceStr = MISSING(t);
    if (balances && balances.length > 0) {
      var b = balances[0];
      var amount = first(
        b && b.current, b && b.available, b && b.balance, b && b.amount, b && b.value,
        b && b.data && b.data.current, b && b.data && b.data.available, b && b.data && b.data.balance
      );
      var cur = first(b && b.currency, currency);
      if (amount !== undefined && amount !== null) {
        const num = Number(amount);

        balanceStr = (isFinite(num) ? formatAmountSk(num) : String(amount)) + ' ' + (cur || '');

        balanceStr = balanceStr.trim();
      }
    }

    var rawForModal = (all && Object.keys(all).length > 0) ? all : accountData;

    return {
      bank: bank || MISSING(t),
      accountName: accountName || MISSING(t),
      currency: currency || MISSING(t),
      iban: iban || MISSING(t),
      balance: balanceStr,
      rawForModal: rawForModal
    };
  };

  _buildOwnerDetail = () => {
    var t = this.props.t;
    var accountData = this.props.accountData || {};
    var all = accountData.allData || {};
    var add = accountData.additionalAccountData || {};

    var name = first(accountData.owner_name, all.name, all.displayName, add.name, MISSING(t));
    var email = first(add.email, MISSING(t));
    var phone = first(add.phone, add['telefón'], add.telefon, MISSING(t));
    var address = first(add.address, add.adresa, MISSING(t));

    var ownerJson = {
      name: name !== MISSING(t) ? name : undefined,
      email: email !== MISSING(t) ? email : undefined,
      phone: phone !== MISSING(t) ? phone : undefined,
      address: address !== MISSING(t) ? address : undefined,
      source: {
        usedFields: {
          owner_name: !!accountData.owner_name,
          allData_name: !!all.name,
          allData_displayName: !!all.displayName,
          additionalAccountData: !!accountData.additionalAccountData
        }
      }
    };

    return { name: name, email: email, phone: phone, address: address, ownerJson: ownerJson };
  };

  // ========= aggregation (simple best-effort) =========

  _filterTxForSelectedMonth = () => {
    var tx = this.props.transactions || [];
    if (!this.state.selectedYm) return tx;
    var sel = this.state.selectedYm;
    return tx.filter(function (t) { return ymKey(t.bookingDate) === sel; });
  };

  _aggregate = function () {
    var rows = [];
    var matchedIds = {};
    var byKey = {}; // key => { item, identifier, txType, count, sum, currency }

    var catById = {};
    var cats = this.props.categories || [];
    for (var i = 0; i < cats.length; i++) catById[cats[i].id] = cats[i];

    var criteria = this.props.criteria || [];
    var tx = this._filterTxForSelectedMonth();

    // normalize + direction helpers

    function lc(s) { return (s || '').toString().toLowerCase(); }

    var acctIban = '';
    if (this.props && this.props.accountData) {
      var ad = this.props.accountData;
      acctIban = ad.iban || (ad.allData && ad.allData.iban) || '';
    }
    acctIban = normIban(acctIban);

    function getDirection(tr) {
      var debtor = normIban(tr.debtorAccount);
      var creditor = normIban(tr.creditorAccount);
      if (acctIban && debtor && debtor === acctIban) return 'debit';
      if (acctIban && creditor && creditor === acctIban) return 'credit';
      var amt = Number(tr.amount || 0);
      if (!isNaN(amt) && amt !== 0) return amt > 0 ? 'credit' : 'debit';
      return 'debit';
    }

    function addRow(key, base, amt, txId) {
      if (!byKey[key]) {
        byKey[key] = {
          key: key,
          item: base.item,
          identifier: base.identifier,
          txType: base.txType, // 'credit' | 'debit'
          count: 0,
          sum: 0,
          currency: base.currency,
          sub: base.sub || false,
          isDefaultBucket: base.isDefaultBucket || false
        };
      }
      byKey[key].count += 1;
      byKey[key].sum += Math.abs(Number(amt || 0));
      if (txId) matchedIds[String(txId)] = true;
    }

    // match tx against criteria
    for (var tIdx = 0; tIdx < tx.length; tIdx++) {
      var tr = tx[tIdx];
      var amount = Number(tr.amount || 0);
      var currency = tr.currency || (this.props.accountData && this.props.accountData.currency) || '';
      var type = getDirection(tr); // 'credit' / 'debit'

      // lowercase text fields for safe matching
      var descLC = lc(tr.remittanceInformationUnstructured);
      var creditorNameLC = lc(tr.creditorName);
      var debtorNameLC = lc(tr.debtorName);

      // IBANs normalized
      var debtorAcc = normIban(tr.debtorAccount);
      var creditorAcc = normIban(tr.creditorAccount);

      var found = false;

      for (var cIdx = 0; cIdx < criteria.length; cIdx++) {
        var cr = criteria[cIdx];

        // normalize inputs
        var crIban = normIban(cr.iban);
        var crNeedle = lc(cr.searchString);

        // match by IBAN (only if criterion IBAN provided)
        var matchIban = !!crIban && (debtorAcc === crIban || creditorAcc === crIban);

        // match by search string (only if provided) in description OR counterparty names
        var matchStr = !!crNeedle && (
          descLC.indexOf(crNeedle) !== -1 ||
          creditorNameLC.indexOf(crNeedle) !== -1 ||
          debtorNameLC.indexOf(crNeedle) !== -1
        );

        if (matchIban || matchStr) {
          var cat = catById[cr.categoryId];
          var catName = cat ? (cat.name || ('Kategória ' + cr.categoryId)) : ('Kategória ' + cr.categoryId);
          var prov = cr.provider || '';
          // if both IBAN and string are present & matched, prefer IBAN for the identifier
          var ident = crIban || cr.searchString || '';
          var mainKey = 'cat:' + cr.categoryId + ':' + type;
          var provKey = 'cat:' + cr.categoryId + ':prov:' + prov + ':' + ident + ':' + type;

          // main category total (no identifier)
          addRow(mainKey, { item: catName, identifier: '', txType: type, currency: currency }, amount, tr.id);
          // provider sub-row (with identifier)
          addRow(provKey, { item: catName, identifier: ident, txType: type, currency: currency, sub: prov }, amount, tr.id);

          found = true;
          // (intentionally no break: allow a tx to match multiple criteria if you configure overlaps)
        }
      }

      if (!found) {
        // bucket to unmatched credit/debit
        var dk = type === 'debit' ? 'debet' : 'credit';
        var defaultLabel = type === 'debit' ? 'Neagregované transakcie (debet)' : 'Neagregované transakcie (kredit)';
        addRow(
          'unmatched:' + dk,
          { item: defaultLabel, identifier: '', txType: type, currency: currency, isDefaultBucket: true },
          amount,
          null
        );
      }
    }

    // monthly balance: credit adds, debit subtracts
    var balance = 0;
    for (var j = 0; j < tx.length; j++) {
      var t2 = tx[j];
      var amt2 = Number(t2.amount || 0);
      var dir2 = getDirection(t2);
      balance += (dir2 === 'debit') ? -Math.abs(amt2) : Math.abs(amt2);
    }
    var curr = (this.props.accountData && this.props.accountData.currency) || (tx[0] && tx[0].currency) || '';

    // flatten rows
    for (var k in byKey) if (Object.prototype.hasOwnProperty.call(byKey, k)) rows.push(byKey[k]);

    // summary rows at bottom
    rows.push({
      key: 'monthly-balance',
      item: 'Mesačný balans na účet',
      identifier: '',
      txType: '',
      count: '',
      sum: balance,
      currency: curr
    });

    return { rows: rows, matchedTxnIds: Object.keys(matchedIds) };
  };

  // ========= purge handling =========

  _openPurgeModal = () => {
    this.setState({ purgeVisible: true, purgeConfirmText: '', purgeLoading: false });
  };

  _closePurgeModal = () => {
    if (this.state.purgeLoading) return;
    this.setState({ purgeVisible: false, purgeConfirmText: '' });
  };

  _confirmPurge = async () => {
    const { onPurgeTriggered } = this.props;
    if (!onPurgeTriggered) return this._closePurgeModal();

    this.setState({ purgeLoading: true });
    try {
      const maybePromise = onPurgeTriggered();
      if (maybePromise && typeof maybePromise.then === 'function') {
        await maybePromise;
      }
      this.setState({ purgeLoading: false, purgeVisible: false, purgeConfirmText: '' });
    } catch (e) {
      // keep modal open, stop spinner; container can also show a toast
      this.setState({ purgeLoading: false });
      // eslint-disable-next-line no-console
      console.error('[ADMIN PSD2] Purge failed:', e);
    }
  };

  // ========= renderers =========

  _renderHeader = () => {
    var t = this.props.t;
    var accounts = this.props.accountIds || [];
    var selectedAccountId = this.state.selectedAccountId;
    var months = this.state.monthsForAccount || [];
    var selectedYm = this.state.selectedYm;
    var ibans = this.props.ibans;

    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12}>
          <div style={{ marginBottom: 8 }}>{t('Vyberte účet')}</div>
          <Select
            style={{ width: '100%' }}
            value={selectedAccountId}
            onChange={(val) => this._selectAccount(val)}
          >
            {accounts.map(function (a) {
              var id = (typeof a === 'string') ? a : (a && a.id) ? a.id : '';
              var foundIban = ibans ? ibans.find((i)=>i.accountId === a) : null;
              var label = foundIban ? foundIban.IBAN : a;
              return <Option key={id} value={id}>{label}</Option>;
            })}
          </Select>
        </Col>
        <Col xs={24} sm={12}>
          <div style={{ marginBottom: 8 }}>{t('Vyberte mesiac')}</div>
          <Select
            style={{ width: '100%' }}
            value={selectedYm}
            disabled={!selectedAccountId}
            onChange={(val) => this.setState({ selectedYm: val })}
          >
            {months.map((k) => (
              <Option key={k} value={k}>{this._monthLabel(k)}</Option>
            ))}
          </Select>
        </Col>
      </Row>
    );
  };

  _renderAccountAndOwner = () => {
    var t = this.props.t;
    var acc = this._buildAccountDetail();
    var owner = this._buildOwnerDetail();

    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card title={t('Detail zvoleného účtu')} bordered>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 8 }}>
              <div>{t('Banka')}</div><div>{acc.bank}</div>
              <div>{t('Názov účtu')}</div><div>{acc.accountName}</div>
              <div>{t('Mena')}</div><div>{acc.currency}</div>
              <div>IBAN</div><div>{acc.iban}</div>
              <div>{t('Aktuálny zostatok')}</div><div>{acc.balance}</div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Button size="small" onClick={() => this.setState({ showAccountJson: true })}>
                {t('JSON dáta')}
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title={t('Údaje o klientovi z banky')} bordered>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 8 }}>
              <div>{t('Meno')}</div><div>{owner.name}</div>
              <div>{t('Email')}</div><div>{owner.email}</div>
              <div>{t('Telefón')}</div><div>{owner.phone}</div>
              <div>{t('Adresa')}</div><div>{owner.address}</div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Button
                size="small"
                onClick={() => this.setState({ showOwnerJson: true, ownerJsonCache: owner.ownerJson })}
              >
                {t('JSON dáta')}
              </Button>
            </div>
          </Card>
        </Col>

        <Modal
          title={t('JSON dáta účtu')}
          visible={this.state.showAccountJson}
          footer={null}
          onCancel={() => this.setState({ showAccountJson: false })}
          width={800}
        >
          <pre style={{ whiteSpace: 'pre-wrap' }}>{pretty(this._buildAccountDetail().rawForModal)}</pre>
        </Modal>

        <Modal
          title={t('JSON dáta – vlastník účtu')}
          visible={this.state.showOwnerJson}
          footer={null}
          onCancel={() => this.setState({ showOwnerJson: false, ownerJsonCache: null })}
          width={800}
        >
          <pre style={{ whiteSpace: 'pre-wrap' }}>{pretty(this.state.ownerJsonCache || {})}</pre>
        </Modal>
      </Row>
    );
  };

  _renderAggregatedTable = () => {
    var t = this.props.t;
    var aggregated = this._aggregate();
    var columns = [
      {
        title: t('Položka'),
        dataIndex: 'item',
        key: 'item',
        render: (val, row) => (
          <span style={row.sub ? {paddingLeft: 16} : {}}>
            {val} {row.sub ? <Tag>{row.sub}</Tag> : null}
            {row.isDefaultBucket ? <Tag>default</Tag> : null}
          </span>
        )
      },
      { title: t('IBAN / Hľadaný reťazec'), dataIndex: 'identifier', key: 'identifier' },
      {
        title: t('Typ'),
        dataIndex: 'txType',
        key: 'txType',
        render: (v) => v === 'credit' ? t('kredit') : (v === 'debit' ? t('debet') : v)
      },
      { title: t('Počet'), dataIndex: 'count', key: 'count' },
      { 
        title: t('Suma'),
        dataIndex: 'sum',
        key: 'sum',
        render: (v) => formatAmountSk(Number(v))
      },
      { title: t('Mena'), dataIndex: 'currency', key: 'currency' }
    ];
    var dataSource = aggregated.rows.map(function (r, idx) {
      var copy = {};
      for (var k in r) copy[k] = r[k];
      copy.key = r.key || idx;
      return copy;
    });

    return (
      <Card title={t('Agregovaný prehľad transakcií')} bordered style={{ marginBottom: 16 }}>
        <Table columns={columns} dataSource={dataSource} pagination={false} />
      </Card>
    );
  };

  _renderMonthlyTransactions = () => {
    var t = this.props.t;
    var aggregated = this._aggregate();
    var matchedSet = {};
    for (var i = 0; i < aggregated.matchedTxnIds.length; i++) {
      matchedSet[String(aggregated.matchedTxnIds[i])] = true;
    }

    var iban = '';
    if (this.props && this.props.accountData) {
      var ad = this.props.accountData;
      iban = ad.iban || (ad.allData && ad.allData.iban) || '';
    }
    iban = normIban(iban);

    var tx = this._filterTxForSelectedMonth();

    var columns = [
      {
        title: t('Dátum'),
        dataIndex: 'bookingDate',
        key: 'bookingDate',
        render: function (v) { return v ? (new Date(v)).toLocaleDateString() : ''; }
      },
      { title: t('Protistrana'), dataIndex: 'counterparty', key: 'counterparty' },
      {
        title: t('Suma'),
        dataIndex: 'amount',
        key: 'amount',
        render: function (v, row) {
          var val = Number(v || 0);
          var isDebit = val < 0 || normIban(row.trRaw.debtorAccount,'') === iban;
          return (isDebit ? '-' : '') + formatAmountSk(Math.abs(val));
        }
      },
      { title: t('Mena'), dataIndex: 'currency', key: 'currency' },
      { title: t('Popis'), dataIndex: 'remittanceInformationUnstructured', key: 'remittanceInformationUnstructured' },
      { title: t('Účet protistrany'), dataIndex: 'counterpartyAccount', key: 'counterpartyAccount' },
      {
        title: t('JSON dáta'),
        key: 'json',
        render: (_, row) => (
          <Button
            size="small"
            onClick={function () {
              Modal.info({
                title: t('Detail transakcie (JSON)'),
                content: <pre style={{ whiteSpace: 'pre-wrap' }}>{pretty(row.trRaw)}</pre>,
                width: 800,
                okText: t('Zavrieť')
              });
            }}
          >
            {t('Zobraziť')}
          </Button>
        )
      }
    ];

    var dataSource = tx.map(function (tr, idx) {
      return {
        key: String(tr.id || idx),
        id: tr.id,
        bookingDate: tr.bookingDate,
        counterparty: getCounterParty(tr,iban),
        amount: (typeof tr.amount === 'string' || typeof tr.amount === 'number') ? tr.amount : '',
        currency: tr.currency,
        remittanceInformationUnstructured: tr.remittanceInformationUnstructured,
        counterpartyAccount: getCounterPartyIban(tr, iban),
        transactionType: tr.transactionType,
        // keep raw fields
        trRaw: tr,
      };
    });

    return (
      <Card title={t('Mesačné transakcie')} bordered>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}          
          rowClassName={function (row) { return matchedSet[String(row.id)] ? 'tx-aggregated' : ''; }}
        />
      </Card>
    );
  };

  render() {
    var t = this.props.t;
    var loading = !!this.props.loading;
    var hasAnyPsd2Data = !!this.props.hasAny;

    const purgeDisabled = this.state.purgeConfirmText !== 'ZMAZAŤ';

    return (
      <Spin spinning={loading}>
        <div style={{ marginBottom: 16 }}>
          {!hasAnyPsd2Data ? (
            <Tag color="orange">{t('Informácie z bankových účtov zatiaľ nie sú získané.')}</Tag>
          ) : null}
        </div>

        <div style={{ marginBottom: 12 }}>
          <Button
            type="danger"
            onClick={this._openPurgeModal}
          >
            {t('Vymazať všetky bankové údaje zo systému')}
          </Button>
        </div>

        {/* Purge confirm modal */}
        <Modal
          title={t('Naozaj vymazať všetky PSD2 údaje?')}
          visible={this.state.purgeVisible}
          onCancel={this._closePurgeModal}
          footer={[
            <Button key="cancel" onClick={this._closePurgeModal} disabled={this.state.purgeLoading}>
              {t('Zrušiť')}
            </Button>,
            <Button
              key="purge"
              type="danger"
              loading={this.state.purgeLoading}
              onClick={this._confirmPurge}
              disabled={purgeDisabled}
            >
              {t('Vymazať')}
            </Button>,
          ]}
        >
          <p style={{ marginBottom: 8 }}>
            {t('Táto akcia nenávratne odstráni všetky stiahnuté údaje z bankových účtov (účty, zostatky, transakcie).')}
          </p>
          <p style={{ marginBottom: 8 }}>
            {t('Ak ste si istý, napíšte do poľa nižšie:')} <b>ZMAZAŤ</b>
          </p>
          <Input
            placeholder="ZMAZAŤ"
            value={this.state.purgeConfirmText}
            onChange={(e) => this.setState({ purgeConfirmText: (e.target.value || '').toUpperCase() })}
            disabled={this.state.purgeLoading}
          />
        </Modal>

        {this._renderHeader()}
        {this._renderAccountAndOwner()}
        {this._renderAggregatedTable()}
        {this._renderMonthlyTransactions()}
      </Spin>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/account-management/components/psd2/Psd2StatementsAdmin.js